import { ISelectItem } from "@components/inputs/select/Select.types";
import { ifAll, ifAny, IFieldDef, isVisibleByPath, not, TGetValueFn, TInfoValue } from "@components/smart/FieldInfo";
import {
    BirthNumberFieldDef,
    countryDef,
    getAddressFields,
    getAddressGroup
} from "@components/smart/GeneralFieldDefinition";
import { createPath, splitPath } from "@odata/BindingContext";
import {
    AddressEntity,
    CsszEmploymentTypeEntity,
    EmploymentEarlyTerminationReasonEntity,
    EmploymentRelationshipTerminationReasonEntity,
    EntitySetName,
    EntityTypeName,
    LaborOfficeEmploymentTypeEntity,
    LORegionalOfficeEntity,
    MpsvFreeAccessToLaborMarketTypeEntity,
    NoUnemploymentSupportTypeEntity,
    PrDisabilityTypeEntity,
    PrForeignInsuranceCarrierEntity,
    PrHealthInsuranceCompanyEntity,
    PrIdentityDocumentTypeEntity,
    PrPensionTypeEntity,
    PrSexEntity,
    PrSubmissionRegZecDetailEntity,
    PrSubmissionRegZecEntity,
    PrWorkPermitTypeEntity,
    RegZecCompanyEntity,
    RegZecDisabilityEntity,
    RegZecEmployeeEntity,
    RegZecEmploymentEntity,
    RegZecForeignInsurerEntity,
    RegZecIdentityDocumentEntity,
    RegZecLaborMarketAccessEntity,
    RegZecPensionEntity,
    RegZecPensionInsuranceIntervalEntity,
    RegZecUnemploymentSupportEntity,
    ServiceRelationshipTerminationReasonEntity,
    WorkplaceTypeEntity,
    YesNoEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    CsszEmploymentTypeCode,
    EmploymentRelationshipTerminationReasonCode,
    LaborOfficeEmploymentTypeCode,
    NoUnemploymentSupportTypeCode,
    PrForeignInsuranceCarrierTypeCode,
    PrPensionTypeCode,
    PrSubmissionTypeCode,
    PrWorkPermitTypeCode,
    YesNoCode
} from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { getForeignTaxIdentifierTypeDef, transformWorkPermitTypeItems } from "@pages/payroll/employee/Employee.utils";
import {
    detailedSpecificationDef,
    getIsceDef,
    getIscoDef,
    shiftPatternDef
} from "@pages/payroll/employment/Employment.utils";
import { educationDef, getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import { getEmploymentSummaryItemDef } from "@pages/payroll/submission/Submission.utils";
import { getSocialSecurityFieldDef } from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import {
    getAddressAutocompleteDef,
    getComboboxDefWithMpsvCode,
    getJmhzAddressIdDef,
    getJmhzIdDef,
    ifTruthy,
    isNonZeroValue,
    oneOfIsDefined
} from "@pages/payroll/submission/submissionTypes/PrJmhzSubmission.utils";
import {
    cleanUpUnavailableFieldsFromGroups,
    getMpsvCodeFromSubType
} from "@pages/payroll/submission/submissionTypes/PrSubmissionRegZec.utils";
import PrSubmissionRegZecFormView from "@pages/payroll/submission/submissionTypes/PrSubmissionRegZecFormView";
import { forEachKey, isDefined } from "@utils/general";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import { ValidationError } from "yup";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { FieldType, ValidatorType } from "../../../../enums";
import { TRecordType } from "../../../../global.types";
import { Model } from "../../../../model/Model";
import { getUtcDayjs } from "../../../../types/Date";
import { addFormDef, addFormGroup, FormType, getFormGroup } from "../../../../views/formView/Form.utils";
import { FormStorage } from "../../../../views/formView/FormStorage";

const checkPathIsNonCZCountry = (storage: Model, path: string): boolean => {
    const cc = storage.getValueByPath(path, { useDirectValue: false });
    return !!(cc && cc !== CountryCode.CzechRepublic);
};

const isForeigner: TGetValueFn<boolean> = (args) =>
    checkPathIsNonCZCountry(args.storage, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.Citizenship));

const hasForeignPermanentAddress: TGetValueFn<boolean> = (args) =>
    checkPathIsNonCZCountry(args.storage, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PermanentAddress, AddressEntity.Country));

const isNonCzechTaxResident: TGetValueFn<boolean> = (args) =>
    checkPathIsNonCZCountry(args.storage, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.CountryOfResidency));

const hasDisabilityType: TGetValueFn<boolean> = (args) =>
    isDefined(args.storage.getValueByPath(
        createPath(
            PrSubmissionRegZecEntity.Detail,
            PrSubmissionRegZecDetailEntity.Disability,
            RegZecDisabilityEntity.PrDisabilityTypeCode)));

const hasApplicableForeignLegislation: TGetValueFn<boolean> = (args) =>
    args.storage.getValueByPath(
        createPath(
            PrSubmissionRegZecEntity.Detail,
            PrSubmissionRegZecDetailEntity.ApplicableForeignLegislationCode)) === YesNoCode.Yes;

const hasFreeAccessToLaborMarket: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const hasAccess = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.HasFreeAccessToLaborMarket), { useDirectValue: false });
    return hasAccess === YesNoCode.Yes;
};

const withoutFreeAccessToLaborMarket: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const hasAccess = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.HasFreeAccessToLaborMarket), { useDirectValue: false });
    return hasAccess === YesNoCode.No;
};

function hasWorkPermitOfType(...types: PrWorkPermitTypeCode[]): TGetValueFn<boolean> {
    return (args) => {
        const { storage } = args;
        const workPermitType = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitType), { useDirectValue: false });
        return types.includes(workPermitType);
    };
}

const hasTerminationBenefitEntitled: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const hasTerminationBenefitEntitled = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled), { useDirectValue: false });
    return hasTerminationBenefitEntitled === YesNoCode.Yes;
};
const isOfLaborOfficeEmploymentType = (type: LaborOfficeEmploymentTypeCode): TGetValueFn<boolean> => {
    return (args) => {
        const { storage } = args;
        const laborOfficeEmploymentType = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType), { useDirectValue: false });
        return laborOfficeEmploymentType === type;
    };
};
const isEndedEmployment: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const dateEnd = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateEnd));
    return isDefined(dateEnd);
};

function isOfEmploymentType(...types: CsszEmploymentTypeCode[]): TGetValueFn<boolean> {
    return (args) => {
        const { storage } = args;
        const employmentType = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.CsszEmploymentType), { useDirectValue: false });
        return isDefined(employmentType) && types.includes(employmentType);
    };
}

function getOnlyOneOfCanBeDefinedValidator(...props: string[]): IFieldDef["validator"] {
    return {
        type: ValidatorType.Custom,
        settings: {
            customValidator: (value, args) => {
                const { storage, bindingContext } = args;
                const parentVal = storage.getValue(bindingContext.getParent());
                const definedProps = props.filter(prop => isDefined(parentVal[prop]));
                if (definedProps?.length > 1) {
                    return new ValidationError(i18next.t("Submission:RegZecForm.BothFieldsCannotBeFilled"), false, args.bindingContext.getNavigationPath(false));
                }
                return true;
            }
        }
    };
}

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrSubmissionRegZecFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployeeRegistration
    }));

    const { subType } = args ?? {};
    const addRegZecFormDef = (def: IDefinition, fieldId: string, fieldDef: Partial<IFieldDef>, fieldDefByType: TRecordType<Partial<IFieldDef>>): void => {
        const mpsvCode = getMpsvCodeFromSubType(subType);
        const defKey = mpsvCode && Object.keys(fieldDefByType)?.find(key => key.includes(mpsvCode));
        // field controls other fields -> therefore it needs to be in the form even if
        // it's not applicable for the current subtype.
        const isMasterField = !!fieldDef.affectedFields?.length;
        if (!isMasterField && mpsvCode && !defKey) {
            // field is not applicable for the current subtype, don't add it to the form
            // if mpsvCode is not defined, that mean we are showing an AuditTrail or it's called from unit test,
            // so we want to return all fields to validate them / show differences
            return ;
        }
        const specificFieldDef = fieldDefByType[defKey];
        const forceAsMasterField = isMasterField && !defKey;
        // if defKey is not defined, we are not showing particular type of the form, but probably audit trail
        // -> don't make the field invisible here.
        const isVisible = !subType;
        const masterFieldDef: Partial<IFieldDef> = forceAsMasterField ? {
            isVisible,
            clearIfInvisible: false,
            useForValidation: false // do not validate, only to show other values...
        } : {};

        addFormDef(def, fieldId, {
            ...fieldDef,
            ...specificFieldDef,
            ...masterFieldDef
        });
    };

    d.form.type = FormType.VirtualizedList;
    d.form.summary.splice(2, 0, getEmploymentSummaryItemDef());

    const mainGroup = getFormGroup(d, PrSubmissionGroup.Main);

    addFormDef(d, PrSubmissionRegZecEntity.DateSubmission, {
        type: FieldType.DateTime,
        isRequired: true,
        fieldSettings: {
            maxDate: () => getUtcDayjs().toDate()
        },
        ...getJmhzIdDef(5)
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.DateChangeOrCorrectionFrom), {
        fieldSettings: {
            maxDate: () => getUtcDayjs().toDate()
        },
        ...getJmhzIdDef(9)
    }, {
        "A3,A4,A5,A6,A7": {
            isRequired: true
        }
    });
    addFormDef(d, createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.SocialSecurity), {
        ...getSocialSecurityFieldDef(),
        isRequired: true,
        ...getJmhzIdDef(4)
    });
    addFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.UserEmail), {});

    mainGroup.title = i18next.t("Submission:RegZecForm.Metadata");
    mainGroup.rows = [[
        { id: createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.SocialSecurity) },
        { id: PrSubmissionRegZecEntity.DateSubmission },
        { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.DateChangeOrCorrectionFrom) },
        { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.UserEmail) },
    ]];

    // **************** Add Employer related fields ****************
    addFormDef(d, createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.LegalName), {
        ...getJmhzIdDef(120)
    });
    addFormDef(d, createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.LegalNumber), {});
    addFormDef(d, createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.CSSZSymbolVariable), {
        ...getJmhzIdDef(221)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employer,
        title: i18next.t("Submission:Form.EmployerIdentifier"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.LegalName) },
            { id: createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.LegalNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Company, RegZecCompanyEntity.CSSZSymbolVariable) },
        ]]
    });

    // **************** Add Employee related fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.MpsvIdentifier), {
        ...getJmhzIdDef(51)
    }, {
        "A1": {},
        "A2,A3,A4,A5,A6,A7,A8": {
            isRequired: true
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.LastName), {
        ...getJmhzIdDef(53)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.FirstName), {
        ...getJmhzIdDef(54)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.TitleBeforeName), {
        ...getJmhzIdDef(55),
        label: i18next.t("Submission:RegZecForm.TitleBeforeName")
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.DateOfBirth), {
        ...getJmhzIdDef(56)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.CountryOfBirth), {
        ...countryDef,
        ...getJmhzIdDef(65)
    }, {
        "A1": {
            isRequired: true
        },
        "A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PlaceOfBirth), {
        ...getJmhzIdDef(66)
    }, {
        "A1": {
            isRequired: true
        },
        "A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PrSex), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrSexEntity.Name
        },
        ...getJmhzIdDef(59)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.Citizenship), {
        ...getJmhzIdDef(67),
        ...countryDef,
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PersonalIdentificationNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.EuSocialInsuranceNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.MinimalEducation) }
        ],
        affectedGroups: [
            PrSubmissionGroup.Foreigner, PrSubmissionGroup.ForeignEmployee
        ]
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PersonalIdentificationNumber), {
        ...BirthNumberFieldDef,
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.EuSocialInsuranceNumber), revalidate: true }
        ],
        ...getJmhzIdDef(57)
    }, {
        "A1": {
            isRequired: (args) => {
                if (!isForeigner(args)) {
                    // mandatory for CZ citizens
                    return true;
                }
                const { storage } = args;
                const oic = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.MpsvIdentifier));
                if (oic) {
                    const sin = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.EuSocialInsuranceNumber));
                    return !sin;
                }
                return false;
            },
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.EuSocialInsuranceNumber), {
        isVisible: isForeigner,
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PersonalIdentificationNumber), revalidate: true }
        ],
        ...getJmhzIdDef(58)
    }, {
        "A1": {
            isRequired: (args) => {
                const { storage } = args;
                const oic = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.MpsvIdentifier));
                if (oic) {
                    const pin = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PersonalIdentificationNumber));
                    return !pin;
                }
                return false;
            },
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.OwnTaxPayerNumber), {
        ...getJmhzIdDef(60)
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.BirthLastName), {
        ...getJmhzIdDef(63)
    }, {
        "A1": { isRequired: true },
        "A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.FormerLastNames), {
        ...getJmhzIdDef(64)
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.PrEducation), {
        ...educationDef,
        ...getJmhzIdDef(91)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employee,
        title: i18next.t("Submission:Form.EmployeeIdentifier"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.MpsvIdentifier) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.LastName) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.FirstName) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.TitleBeforeName) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.DateOfBirth) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.CountryOfBirth) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PlaceOfBirth) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PrSex) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.Citizenship) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PersonalIdentificationNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.EuSocialInsuranceNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.OwnTaxPayerNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.BirthLastName) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.FormerLastNames) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.PrEducation) },
        ]]
    });

    // **************** Foreigner identifier ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IdentificationDocumentType), {
        type: FieldType.ComboBox,
        isVisible: isForeigner,
        fieldSettings: {
            displayName: PrIdentityDocumentTypeEntity.Name
        },
        ...getJmhzIdDef(69)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IDNumber), {
        isVisible: isForeigner,
        ...getJmhzIdDef(70)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IDAuthorityOfIssue), {
        isVisible: isForeigner,
        ...getJmhzIdDef(71)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IDCountryOfIssue), {
        ...countryDef,
        isVisible: isForeigner,
        ...getJmhzIdDef(72)
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Foreigner,
        title: i18next.t("Submission:RegZecForm.ForeignerIdentifier"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IdentificationDocumentType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IDNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IDAuthorityOfIssue) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IdentityDocument, RegZecIdentityDocumentEntity.IDCountryOfIssue) },
        ]]
    });

    // **************** PermanentAddress fields ****************
    const permanentAddressPath = createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.PermanentAddress);
    const permanentAddressPaths = getAddressGroup(permanentAddressPath, { multiline: false, addHouseStreetNumber: true })[0];
    const permanentAddressFields = getAddressFields(permanentAddressPath, {
        defaultValue: undefined,
    }, { addHouseStreetNumber: true });
    const permanentAddressIds: TRecordType<number> = {
        "Street": 77,
        "HouseNumber": 78,
        "StreetNumber": 79,
        "City": 80,
        "PostalCode": 82,
        "Country": 83,
    };

    const permanentAddressProps = permanentAddressPaths.map(item => splitPath(item.id).pop()).filter(path => path !== "Country");
    const _getPermanentAddressIsRequiredFlag = (fieldPath: string, isRequired: boolean): TInfoValue<boolean> => {
        const lastPath = splitPath(fieldPath).pop();
        switch (lastPath) {
            case "City":
            case "HouseNumber":
            case "PostalCode":
            case "Country":
                return isRequired ? isRequired
                    : oneOfIsDefined(bc => permanentAddressProps.map(p => bc.getParent().navigate(p)));
            default:
                return false;
        }
    };

    forEachKey(permanentAddressFields, (fieldId) => {
        const lastPath = splitPath(fieldId).pop();
        addRegZecFormDef(d, fieldId, {
            ...permanentAddressFields[fieldId],
            ...getJmhzIdDef(permanentAddressIds[lastPath]),
            affectedGroups: [
                PrSubmissionGroup.CzechAddress
            ],
            affectedFields: [
                ...permanentAddressPaths
            ]
        }, {
            "A1": {
                isRequired: _getPermanentAddressIsRequiredFlag(fieldId, true)
            },
            "A3,A4": {
                isRequired: _getPermanentAddressIsRequiredFlag(fieldId, false)
            }
        });
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.PermanentAddress,
        title: i18next.t("Submission:RegZecForm.PermanentAddress"),
        rows: [[
            ...permanentAddressPaths
        ]]
    });

    // **************** CzechAddress fields ****************
    const localAddressPath = createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.CzechAddress);
    const localAddressPaths = getAddressGroup(localAddressPath, { multiline: false, addHouseStreetNumber: true, withoutCountry: true })[0];
    const localAddressFields = getAddressFields(localAddressPath, {
        defaultValue: undefined,
        isVisible: hasForeignPermanentAddress,
    }, { addHouseStreetNumber: true, withoutCountry: true });

    const localAddressProps = localAddressPaths.map(item => splitPath(item.id).pop());
    const _getLocalAddressIsRequiredFlag = (fieldPath: string): TInfoValue<boolean> => {
        const lastPath = splitPath(fieldPath).pop();
        switch (lastPath) {
            case "City":
            case "HouseNumber":
            case "PostalCode":
                return oneOfIsDefined(bc => localAddressProps.map(p => bc.getParent().navigate(p)));
            default:
                return false;
        }
    };

    forEachKey(localAddressFields, (fieldId) => {
        addRegZecFormDef(d, fieldId, {
            ...localAddressFields[fieldId],
            ...getJmhzAddressIdDef(fieldId, 513),
            affectedFields: [
                ...localAddressPaths
            ]
        }, {
            "A1,A3,A4": { isRequired: _getLocalAddressIsRequiredFlag(fieldId) }
        });
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.CzechAddress,
        title: i18next.t("Submission:RegZecForm.CzechAddress"),
        rows: [[
            ...localAddressPaths
        ]]
    });

    // **************** Tax Identification fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.CountryOfResidency), {
        ...getJmhzIdDef(68),
        ...countryDef,
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignTaxIdentifierType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignTaxIdentifier) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.DateResidencyFrom) },
        ]
    }, {
        "A1": {
            isRequired: true
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.DateResidencyFrom), {
        ...getJmhzIdDef(459)
    }, {
        "A3,A4": {
            isRequired: true,
            isVisible: oneOfIsDefined(bc => [bc.getParent().navigate(RegZecEmployeeEntity.CountryOfResidency)])
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignTaxIdentifierType), {
        ...getForeignTaxIdentifierTypeDef(),
        isVisible: isNonCzechTaxResident,
        ...getJmhzIdDef(61)
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignTaxIdentifier), {
        isVisible: isNonCzechTaxResident,
        ...getJmhzIdDef(62)
    }, {
        "A1,A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.CountryOfResidency,
        title: i18next.t("Submission:RegZecForm.CountryOfResidency"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.CountryOfResidency) },
            { id: createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.DateResidencyFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignTaxIdentifierType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignTaxIdentifier) },
        ]]
    });

    // **************** TaxResidency address fields ****************
    const taxResidenceAddressPath = createPath(PrSubmissionRegZecEntity.Employee, RegZecEmployeeEntity.TaxResidenceAddress);
    const taxResidenceAddressPaths = getAddressGroup(taxResidenceAddressPath, { multiline: false, addHouseStreetNumber: true })[0];
    const taxResidenceAddressFields = getAddressFields(taxResidenceAddressPath, {
        isVisible: isNonCzechTaxResident,
        defaultValue: undefined,
        affectedFields: [
            ...taxResidenceAddressPaths
        ]
    }, { addHouseStreetNumber: true });

    const _getTaxResidenceAddressIsRequiredFlag = (fieldPath: string): TInfoValue<boolean> => {
        const lastPath = splitPath(fieldPath).pop();
        switch (lastPath) {
            case "City":
            case "HouseNumber":
            case "PostalCode":
            case "Country":
                return true;
            default:
                return false;
        }
    };

    forEachKey(taxResidenceAddressFields, (fieldId) => {
        addRegZecFormDef(d, fieldId, {
            ...taxResidenceAddressFields[fieldId],
            ...getJmhzAddressIdDef(fieldId, 519),
        }, {
            "A1,A3,A4": {
                isRequired: _getTaxResidenceAddressIsRequiredFlag(fieldId)
            }
        });
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.TaxResidenceAddress,
        title: i18next.t("Submission:RegZecForm.TaxResidenceAddress"),
        rows: [[
            ...taxResidenceAddressPaths
        ]]
    });

    // **************** Disability fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.PrDisabilityType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrDisabilityTypeEntity.Name
        },
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.DateDisabilityFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.DateDisabilityTo) },
        ],
        ...getJmhzIdDef(85)
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.DateDisabilityFrom), {
        ...getJmhzIdDef(86),
        isVisible: hasDisabilityType,
    }, {
        "A1": { isRequired: true },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.DateDisabilityTo), {
        ...getJmhzIdDef(87),
        isVisible: hasDisabilityType,
    }, {
        "A1,A3,A4": { isRequired: false }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IsZtpP), {
        ...getJmhzIdDef(90),
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
    }, {
        "A1": {
            isRequired: oneOfIsDefined(bc =>
                [bc.getParent().navigate(PrSubmissionRegZecDetailEntity.Disability).navigate(RegZecDisabilityEntity.PrDisabilityType)])
        },
        "A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Disability,
        title: i18next.t("Submission:RegZecForm.Disability"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.PrDisabilityType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.DateDisabilityFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Disability, RegZecDisabilityEntity.DateDisabilityTo) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.IsZtpP) },
        ]]
    });

    // **************** Health Insurance fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.HealthInsuranceCompany), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrHealthInsuranceCompanyEntity.ShortName
        },
        ...getJmhzIdDef(102)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {} // available, optional
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.CurrentSocialInsuranceProviderName), {
        ...getJmhzIdDef(103)
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.PreviousSocialInsuranceProviderName), {
        ...getJmhzIdDef(104)
    }, {
        "A1,A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.HealthInsurance,
        title: i18next.t("Submission:RegZecForm.HealthInsurance"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.HealthInsuranceCompany) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.CurrentSocialInsuranceProviderName) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.PreviousSocialInsuranceProviderName) },
        ]]
    });

    // **************** Employment fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.MpsvIdentifier), {
        ...getJmhzIdDef(228),
    }, {
        "A2,A3,A4,A5,A6,A7,A8": {
            isRequired: true
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateStart), {
        ...getJmhzIdDef(223)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateContractFrom), {
        ...getJmhzIdDef(227),
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateEnd), {
        ...getJmhzIdDef(224),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsEndedByDeath) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.AverageNetMonthlyEarnings) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.ServiceRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentEarlyTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.IsSeverancePaidInFull) },
        ]
    }, {
        "A2": {
            isRequired: true,
        },
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsEndedByDeath), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        isVisible: isEndedEmployment,
        ...getJmhzIdDef(225),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.AverageNetMonthlyEarnings) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.ServiceRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled) },
        ]
    }, {
        "A2": {
            isRequired: true,
        },
        "A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsSmallScaleEmployment), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(243)
    }, {
        "A1": {
            isRequired: true,
        },
        "A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.CsszEmploymentType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: CsszEmploymentTypeEntity.Name
        },
        ...getJmhzIdDef(239),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason) },
        ]
    }, {
        "A1,A2,A3,A4": {
            isRequired: true,
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DetailedSpecification), {
        ...detailedSpecificationDef,
        ...getJmhzIdDef(502)
    }, {
        "A1,A2,A3,A4": { isRequired: true }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.Isco), {
        ...getIscoDef(),
        ...getJmhzIdDef(234)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.MinimalEducation), {
        ...educationDef,
        ...getJmhzIdDef(248)
    }, {
        "A1": {
            isRequired: isForeigner,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.PositionName), {
        ...getJmhzIdDef(235),
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsManagerialEmployee), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(238)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.Isce), {
        ...getIsceDef(),
        ...getJmhzIdDef(249)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.ContractPlaceOfWork), {
        ...getJmhzIdDef(527),
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.City), {
        ...getJmhzIdDef(528),
        ...getAddressAutocompleteDef("City"),
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.CityCode), {
        ...getJmhzIdDef(529),
        ...getAddressAutocompleteDef("AddressCode"),
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.ExpectedPlaceOfWork), {
        ...getJmhzIdDef(526)
    }, {
        "A1": {
            isRequired: isForeigner,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.ShiftPattern), {
        ...shiftPatternDef,
        ...getJmhzIdDef(255)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsContinuousOperation), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(407)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.WorkplaceType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: WorkplaceTypeEntity.Name
        },
        ...getJmhzIdDef(258)
    }, {
        "A1": {
            isRequired: oneOfIsDefined(bc => [
                bc.getParent().getParent()
                    .navigate(PrSubmissionRegZecEntity.Detail)
                    .navigate(PrSubmissionRegZecDetailEntity.Disability)
                    .navigate(RegZecDisabilityEntity.PrDisabilityType)
            ])
        },
        "A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employment,
        title: i18next.t("Submission:RegZecForm.Employment"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.MpsvIdentifier) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateStart) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateContractFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateEnd) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsEndedByDeath) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsSmallScaleEmployment) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.CsszEmploymentType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DetailedSpecification) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.Isco) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.MinimalEducation) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.PositionName) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsManagerialEmployee) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.Isce) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.ContractPlaceOfWork) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.City) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.CityCode) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.ExpectedPlaceOfWork) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.ShiftPattern) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.IsContinuousOperation) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.WorkplaceType) },
        ]]
    });

    // **************** Foreign carrier fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrForeignInsuranceCarrierEntity.Name,
            transformFetchedItems: items =>
                items.filter(i => i.id !== PrForeignInsuranceCarrierTypeCode.None)
        },
        ...getJmhzIdDef(92)
    }, {
        "A1": {
            // isRequired: isOfEmploymentType("N"),
        },
        "A3,A4": {},
        "A6,A7": { isRequired: true }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierName), {
        ...getJmhzIdDef(93)
    }, {
        "A1,A3,A4": {},
        "A6,A7": { isRequired: true }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceNumber), {
        ...getJmhzIdDef(100)
    }, {
        "A1,A3,A4,A6": {},
        "A7": { isRequired: true }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierSectorType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: "Name"
        },
        ...getJmhzIdDef(101)
    }, {
        "A1,A3,A4,A6,A7": {}
    });

    const foreignInsuranceAddressPath = createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierAddress);
    const foreignInsuranceAddressPaths = (getAddressGroup(foreignInsuranceAddressPath, { multiline: false, addHouseStreetNumber: true })[0]);

    const foreignInsuranceAddressFields = getAddressFields(foreignInsuranceAddressPath, {
        defaultValue: undefined,
        affectedFields: [
            ...foreignInsuranceAddressPaths
        ]
    }, { addHouseStreetNumber: true });

    const foreignInsurerAddressProps = foreignInsuranceAddressPaths.map(f => splitPath(f.id).pop()).filter(id => id !== "Country");
    const foreignInsurerProps = [
        RegZecForeignInsurerEntity.InsuranceCarrierType,
        RegZecForeignInsurerEntity.InsuranceCarrierName,
        RegZecForeignInsurerEntity.InsuranceNumber,
        RegZecForeignInsurerEntity.InsuranceCarrierSectorType,
    ];

    const _getForeignInsurerAddressIsRequiredFlag = (fieldPath: string): TInfoValue<boolean> => {
        const lastPath = splitPath(fieldPath).pop();
        switch (lastPath) {
            case "City":
            case "HouseNumber":
            case "PostalCode":
                return oneOfIsDefined(bc => foreignInsurerAddressProps.map(p => bc.getParent().navigate(p)));
            case "Country":
                return ifAny(
                    oneOfIsDefined(bc => foreignInsurerProps.map(p => bc.getParent().getParent().navigate(p))),
                    oneOfIsDefined(bc => foreignInsurerAddressProps.map(p => bc.getParent().navigate(p)))
                );
            default:
                return false;
        }
    };

    forEachKey(foreignInsuranceAddressFields, (fieldId) => {
        addRegZecFormDef(d, fieldId, {
            ...foreignInsuranceAddressFields[fieldId],
            ...getJmhzAddressIdDef(fieldId, 94)
        }, {
            "A1,A3,A4,A6,A7": {
                isRequired: _getForeignInsurerAddressIsRequiredFlag(fieldId),
            }
        });
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.ForeignCarrier,
        title: i18next.t("Submission:RegZecForm.ForeignCarrier"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierName) },
            ...foreignInsuranceAddressPaths,
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ForeignInsurer, RegZecForeignInsurerEntity.InsuranceCarrierSectorType) },
        ]]
    });

    const hasPension: TGetValueFn<boolean> = (args) => {
        const { storage } = args;
        const pensionType = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.PensionTypeCode));
        return isDefined(pensionType) && pensionType !== PrPensionTypeCode.DoesNotReceive;
    };

    // **************** Pension fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.PensionType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrPensionTypeEntity.Name,
            transformFetchedItems: (items: ISelectItem[]) => items.filter(i => i.id !== PrPensionTypeCode.DoesNotReceive)
        },
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.DatePensionFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.IsEarlyRetirement) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.HasReducedRetirementAge) }
        ],
        ...getJmhzIdDef(113)
    }, {
        "A1,A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.DatePensionFrom), {
        ...getJmhzIdDef(114)
    }, {
        "A1": {
            isRequired: true,
            isVisible: hasPension,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.IsEarlyRetirement), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(115)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {
            isRequired: false
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.HasReducedRetirementAge), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(504)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {
            isRequired: false
        }
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Pension,
        title: i18next.t("Submission:RegZecForm.Pension"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.PensionType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.DatePensionFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.IsEarlyRetirement) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.Pension, RegZecPensionEntity.HasReducedRetirementAge) },
        ]]
    });

    // **************** Employee Without Czech Residency fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.HasFreeAccessToLaborMarket), {
        type: FieldType.ComboBox,
        isVisible: isForeigner,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(414),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.FreeAccessType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.PermitIssuingOffice) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidTo) },
        ]
    }, {
        "A1": {
            isRequired: true,
            defaultValue: YesNoCode.No
        },
        "A3,A4": {
            isRequired: false
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.FreeAccessType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: MpsvFreeAccessToLaborMarketTypeEntity.Name
        },
        ...getJmhzIdDef(105)
    }, {
        "A1,A3,A4": {
            isRequired: true,
            isVisible: ifAll(isForeigner, hasFreeAccessToLaborMarket),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrWorkPermitTypeEntity.Name,
            // shouldDisplayAdditionalColumns: true,
            transformFetchedItems: transformWorkPermitTypeItems
        },
        ...getJmhzIdDef(106),
        // columns: [{ id: PrWorkPermitTypeEntity.MpsvCode }, { id: PrWorkPermitTypeEntity.Name }],
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.PermitIssuingOffice) },
        ]
    }, {
        "A1,A3,A4": {
            isRequired: true,
            isVisible: ifAll(isForeigner, withoutFreeAccessToLaborMarket),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.PermitIssuingOffice), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: LORegionalOfficeEntity.Name,
        },
        ...getJmhzIdDef(107)
    }, {
        "A1,A3,A4": {
            isRequired: true,
            isVisible: ifAll(isForeigner, withoutFreeAccessToLaborMarket, hasWorkPermitOfType(PrWorkPermitTypeCode.WorkPermit)),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitNumber), {
        ...getJmhzIdDef(108),
    }, {
        "A1,A3,A4": {
            isRequired: true,
            isVisible: ifAll(isForeigner, withoutFreeAccessToLaborMarket),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidFrom), {
        ...getJmhzIdDef(109),
    }, {
        "A1,A3,A4": {
            isRequired: true,
            isVisible: ifAll(isForeigner, withoutFreeAccessToLaborMarket),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidTo), {
        ...getJmhzIdDef(110),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentEarlyTerminationReason) }
        ]
    }, {
        "A1,A3,A4": {
            isRequired: true,
            isVisible: ifAll(isForeigner, withoutFreeAccessToLaborMarket),
        }
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.ForeignEmployee,
        title: i18next.t("Submission:RegZecForm.ForeignEmployee"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.HasFreeAccessToLaborMarket) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.FreeAccessType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitType) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.PermitIssuingOffice) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.WorkPermitNumber) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidFrom) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidTo) },
        ]]
    });

    // **************** Employee Without Czech Residency fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ApplicableForeignLegislation), {
        type: FieldType.ComboBox,
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ApplicableForeignLegislationCountry) }
        ],
        ...getJmhzIdDef(427)
    }, {
        "A1": {
            isRequired: true,
        },
        "A3,A4": {}
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ApplicableForeignLegislationCountry), {
        ...countryDef,
        ...getJmhzIdDef(428)
    }, {
        "A1": {
            isRequired: true,
            isVisible: hasApplicableForeignLegislation,
            clearIfInvisible: true,
        },
        "A3,A4": {}
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.ForeignLegislation,
        title: i18next.t("Submission:RegZecForm.ForeignLegislation"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ApplicableForeignLegislation) },
            { id: createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.ApplicableForeignLegislationCountry) }
        ]]
    });

    // **************** End of Employment fields ****************
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.NoUnemploymentSupportType), {
        ...getComboboxDefWithMpsvCode(NoUnemploymentSupportTypeEntity),
        ...getJmhzIdDef(376),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.AverageNetMonthlyEarnings) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.ServiceRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled) },
        ]
    }, {
        "A1,A2,A4": {
            /* optional in these types */
            isRequired: false,
            isVisible: isEndedEmployment
        }
    });

    // visibility callbacks for UnemploymentSupport
    const hasUnemploymentSupportType: TGetValueFn<boolean> = oneOfIsDefined(bc => [bc.getParent().navigate(RegZecUnemploymentSupportEntity.NoUnemploymentSupportType)]);
    const isEndedByDeath: TGetValueFn<boolean> = ifTruthy(bc => bc.getParent().getParent().navigate(RegZecEmploymentEntity.IsEndedByDeath));
    const isSpecialEmploymentType: TGetValueFn<boolean> = isOfEmploymentType(
        CsszEmploymentTypeCode.MemberOfAssociation,
        CsszEmploymentTypeCode.Procurator,
        CsszEmploymentTypeCode.MemberOfBoard,
        CsszEmploymentTypeCode.InsuranceAssessor,
        CsszEmploymentTypeCode.Partner_Executive_LimitedPartner_DirectorOfALlc_AndPersonInChargeOfBusinessManagement,
    );
    const isPensionOrNoInsurance: TGetValueFn<boolean> = (args) => {
        const noUnSupportType = args.storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.NoUnemploymentSupportType), { useDirectValue: false });
        return noUnSupportType === NoUnemploymentSupportTypeCode.PensionOrNoInsurance;
    };

    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType), {
        ...getComboboxDefWithMpsvCode(LaborOfficeEmploymentTypeEntity),
        ...getJmhzIdDef(525),
        isRequired: true,
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.ServiceRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.SeverancePayAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.RetirementBenefitAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.TerminationSettlementAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentEarlyTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason) },
        ]
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(isEndedEmployment, not(ifAny(hasUnemploymentSupportType, isEndedByDeath, isSpecialEmploymentType)))
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.AverageNetMonthlyEarnings), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(377),
        isRequired: true,
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(isEndedEmployment, not(ifAny(isPensionOrNoInsurance, isEndedByDeath)))
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason), {
        ...getComboboxDefWithMpsvCode(EmploymentRelationshipTerminationReasonEntity),
        ...getJmhzIdDef(380),
        isRequired: true,
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled) },
        ]
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(isOfLaborOfficeEmploymentType(LaborOfficeEmploymentTypeCode.Employment),
                isEndedEmployment,
                not(ifAny(
                    hasUnemploymentSupportType,
                    isEndedByDeath,
                    isSpecialEmploymentType
                )))
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.ServiceRelationshipTerminationReason), {
        ...getComboboxDefWithMpsvCode(ServiceRelationshipTerminationReasonEntity),
        ...getJmhzIdDef(381),
        isRequired: true,
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(
                isOfLaborOfficeEmploymentType(LaborOfficeEmploymentTypeCode.ServiceAct234),
                isEndedEmployment,
                not(ifAny(
                    hasUnemploymentSupportType,
                    isEndedByDeath
                ))
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.PensionInsuranceIntervals, RegZecPensionInsuranceIntervalEntity.DateFrom), {
        ...getJmhzIdDef(386),
        isRequired: true,
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(
                isEndedEmployment,
                not(ifAny(
                    isPensionOrNoInsurance,
                    ifTruthy(bc => bc.getParent().getParent().getParent().navigate(RegZecEmploymentEntity.IsEndedByDeath))
                ))
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.PensionInsuranceIntervals, RegZecPensionInsuranceIntervalEntity.DateTo), {
        ...getJmhzIdDef(387),
        isRequired: true,
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(
                isEndedEmployment,
                not(ifAny(
                    isPensionOrNoInsurance,
                    ifTruthy(bc => bc.getParent().getParent().getParent().navigate(RegZecEmploymentEntity.IsEndedByDeath))
                ))
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled), {
        type: FieldType.ComboBox,
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        isRequired: true,
        ...getJmhzIdDef(378),
        affectedFields: [
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.SeverancePayAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.RetirementBenefitAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.TerminationSettlementAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.IsSeverancePaidInFull) },
        ]
    }, {
        "A1,A2,A4": {
            isRequired: (args) => {
                const terminationReason = args.storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason), { useDirectValue: false });
                return [
                    EmploymentRelationshipTerminationReasonCode.Organizational,
                    EmploymentRelationshipTerminationReasonCode.HealthReasons,
                ].includes(terminationReason);
            },
            isVisible: ifAll(isEndedEmployment,
                not(ifAny(
                    hasUnemploymentSupportType,
                    isEndedByDeath
                ))
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.IsSeverancePaidInFull), {
        type: FieldType.ComboBox,
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(379),
        isRequired: true
    }, {
        "A1,A2,A4": {
            isVisible: ifAll(
                isEndedEmployment,
                hasTerminationBenefitEntitled
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(530),
        validator: getOnlyOneOfCanBeDefinedValidator(RegZecUnemploymentSupportEntity.SeverancePayAmount, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount),
        affectedFields: [
            {
                id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.SeverancePayAmount),
                revalidate: true
            }
        ]
    }, {
        "A1,A2,A4": {
            isRequired: not(isNonZeroValue(bc => bc.getParent().navigate(RegZecUnemploymentSupportEntity.SeverancePayAmount))),
            isVisible: ifAll(
                isOfLaborOfficeEmploymentType(LaborOfficeEmploymentTypeCode.Employment),
                hasTerminationBenefitEntitled
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.SeverancePayAmount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(531),
        validator: getOnlyOneOfCanBeDefinedValidator(RegZecUnemploymentSupportEntity.SeverancePayAmount, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount),
        affectedFields: [
            {
                id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount),
                revalidate: true
            }
        ]
    }, {
        "A1,A2,A4": {
            isRequired: not(isNonZeroValue(bc => bc.getParent().navigate(RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount))),
            isVisible: ifAll(
                isOfLaborOfficeEmploymentType(LaborOfficeEmploymentTypeCode.Employment),
                hasTerminationBenefitEntitled
            ),
        }
    });

    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.RetirementBenefitAmount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(532),
        validator: getOnlyOneOfCanBeDefinedValidator(RegZecUnemploymentSupportEntity.RetirementBenefitAmount, RegZecUnemploymentSupportEntity.TerminationSettlementAmount),
        affectedFields: [
            {
                id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.TerminationSettlementAmount),
                revalidate: true
            }
        ]
    }, {
        "A1,A2,A4": {
            isRequired: not(isNonZeroValue(bc => bc.getParent().navigate(RegZecUnemploymentSupportEntity.TerminationSettlementAmount))),
            isVisible: ifAll(
                isOfLaborOfficeEmploymentType(LaborOfficeEmploymentTypeCode.ServiceAct234),
                hasTerminationBenefitEntitled
            ),
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.TerminationSettlementAmount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(533),
        validator: getOnlyOneOfCanBeDefinedValidator(RegZecUnemploymentSupportEntity.RetirementBenefitAmount, RegZecUnemploymentSupportEntity.TerminationSettlementAmount),
        affectedFields: [
            {
                id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.RetirementBenefitAmount),
                revalidate: true
            }
        ]
    }, {
        "A1,A2,A4": {
            isRequired: not(isNonZeroValue(bc => bc.getParent().navigate(RegZecUnemploymentSupportEntity.RetirementBenefitAmount))),
            isVisible: ifAll(
                isOfLaborOfficeEmploymentType(LaborOfficeEmploymentTypeCode.ServiceAct234),
                hasTerminationBenefitEntitled
            )
        }
    });
    addRegZecFormDef(d, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentEarlyTerminationReason), {
        ...getComboboxDefWithMpsvCode(EmploymentEarlyTerminationReasonEntity),
        ...getJmhzIdDef(534),
    }, {
        "A1": {
            isRequired: true,
            isVisible: isEndedEmployment
        },
        "A2": {
            isRequired: true,
            isVisible: ifAll(
                isEndedEmployment,
                isForeigner,
                // commented https://solitea-cz.atlassian.net/browse/DEV-45057 - not ON BE for A2, visibility cannot be driven by that fields.
                // hasWorkPermitOfType(PrWorkPermitTypeCode.BlueCard, PrWorkPermitTypeCode.EmployeeCard, PrWorkPermitTypeCode.WorkPermit),
                // (args) => {
                //     const { storage } = args;
                //     const dateEmploymentEnd = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.DateEnd));
                //     const dateWorkPermitValidity = storage.getValueByPath(createPath(PrSubmissionRegZecEntity.Detail, PrSubmissionRegZecDetailEntity.LaborMarketAccess, RegZecLaborMarketAccessEntity.DateValidTo));
                //     return getUtcDayjs(dateEmploymentEnd).isBefore(dateWorkPermitValidity, "date");
                // }
            )
        },
        "A4": {
            /* Optional */
            isVisible: isEndedEmployment
        }
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.EndOfEmployment,
        title: i18next.t("Submission:RegZecForm.EndOfEmployment"),
        rows: [[
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.NoUnemploymentSupportType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.LaborOfficeEmploymentType) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.AverageNetMonthlyEarnings) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.ServiceRelationshipTerminationReason) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.HasTerminationBenefitEntitled) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.IsSeverancePaidInFull) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.MedicalLossCompensationAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.SeverancePayAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.RetirementBenefitAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.TerminationSettlementAmount) },
            { id: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.EmploymentEarlyTerminationReason) },
        ]],
        lineItems: {
            collection: createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.PensionInsuranceIntervals),
            columns: [
                { id: RegZecPensionInsuranceIntervalEntity.DateFrom },
                { id: RegZecPensionInsuranceIntervalEntity.DateTo },
            ],
            canAdd: (args) => {
                const { storage } = args;
                return isVisibleByPath(storage as FormStorage, createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.UnemploymentSupport, RegZecUnemploymentSupportEntity.PensionInsuranceIntervals, RegZecPensionInsuranceIntervalEntity.DateFrom));
            },
            order: RegZecPensionInsuranceIntervalEntity.Id
        }
    });

    cleanUpUnavailableFieldsFromGroups(d);

    if (subType) {
        // change the definition ID according to subtype as it differs -> resets form
        d.form.id = `${d.form.id}_${subType}`;
    }

    return {
        ...d,
        entitySet: EntitySetName.PrSubmissionsRegZec
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
];
setDefByEntityType(EntityTypeName.PrSubmissionRegZec, getDefinitions);
